import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { MONTHS } from './constants';

const ACC = [['cheque', 'Cheque / Current'], ['savings', 'Savings'], ['transmission', 'Transmission']];
export const EMPTY_INFO = {
  registeredName: '', tradingName: '', registrationNumber: '', taxNumber: '', vatNumber: '', payeNumber: '', financialYearEndMonth: null,
  physicalAddress: '', postalAddress: '', email: '', phone: '', employees: null, accountingSoftware: '', previousAccountant: '',
  previousAccountantEmail: '', efilingAccess: false, people: [], debit: { accountHolder: '', bank: '', accountNumber: '', branchCode: '', accountType: '', debitDay: '' },
};

const Field = ({ id, label, value, onChange, type = 'text', ...rest }) => (
  <div>
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <Input id={id} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1 h-10" data-testid={`${id}-input`} {...rest}/>
  </div>
);

const H = ({ children }) => <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mt-2">{children}</div>;

export const InfoForm = ({ value, onChange, needsDebit, peopleLabel = 'Directors / members / trustees' }) => {
  const v = { ...EMPTY_INFO, ...value, debit: { ...EMPTY_INFO.debit, ...(value?.debit || {}) } };
  const set = (k) => (x) => onChange({ ...v, [k]: x });
  const setD = (k) => (x) => onChange({ ...v, debit: { ...v.debit, [k]: x } });
  const setP = (i, k) => (x) => onChange({ ...v, people: v.people.map((p, j) => (j === i ? { ...p, [k]: x } : p)) });
  return (
    <div className="space-y-4" data-testid="onboarding-info-form">
      <H>Business details</H>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field id="info-registeredName" label="Registered name" value={v.registeredName} onChange={set('registeredName')}/>
        <Field id="info-tradingName" label="Trading name" value={v.tradingName} onChange={set('tradingName')}/>
        <Field id="info-registrationNumber" label="Registration number" value={v.registrationNumber} onChange={set('registrationNumber')} placeholder="2019/123456/07"/>
        <div>
          <Label className="text-xs">Financial year-end</Label>
          <Select value={v.financialYearEndMonth ? String(v.financialYearEndMonth) : ''} onValueChange={(x) => onChange({ ...v, financialYearEndMonth: Number(x) })}>
            <SelectTrigger className="mt-1 h-10" data-testid="info-fye-select"><SelectValue placeholder="Choose month"/></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Field id="info-taxNumber" label="Income tax number" value={v.taxNumber} onChange={set('taxNumber')}/>
        <Field id="info-vatNumber" label="VAT number (if registered)" value={v.vatNumber} onChange={set('vatNumber')}/>
        <Field id="info-payeNumber" label="PAYE number (if registered)" value={v.payeNumber} onChange={set('payeNumber')}/>
        <Field id="info-employees" label="Number of employees" type="number" min="0" value={v.employees ?? ''} onChange={(x) => onChange({ ...v, employees: x === '' ? null : Number(x) })}/>
        <div className="sm:col-span-2"><Field id="info-physicalAddress" label="Physical business address" value={v.physicalAddress} onChange={set('physicalAddress')}/></div>
        <div className="sm:col-span-2"><Field id="info-postalAddress" label="Postal address (if different)" value={v.postalAddress} onChange={set('postalAddress')}/></div>
        <Field id="info-email" label="Accounts email" type="email" value={v.email} onChange={set('email')}/>
        <Field id="info-phone" label="Phone" type="tel" value={v.phone} onChange={set('phone')}/>
        <Field id="info-accountingSoftware" label="Accounting software used" value={v.accountingSoftware} onChange={set('accountingSoftware')} placeholder="Xero, Sage, QuickBooks…"/>
        <Field id="info-previousAccountant" label="Previous accountant" value={v.previousAccountant} onChange={set('previousAccountant')}/>
        <div className="sm:col-span-2"><Field id="info-previousAccountantEmail" label="Previous accountant email (for professional clearance)" type="email" value={v.previousAccountantEmail} onChange={set('previousAccountantEmail')}/></div>
      </div>

      <H>{peopleLabel}</H>
      <div className="space-y-2">
        {v.people.map((p, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.4fr_1.2fr_1fr_1.4fr_auto] gap-2 items-end" data-testid={`info-person-${i}`}>
            <Field id={`info-person-${i}-name`} label="Full name" value={p.name} onChange={setP(i, 'name')}/>
            <Field id={`info-person-${i}-id`} label="ID / passport no." value={p.idNumber} onChange={setP(i, 'idNumber')}/>
            <Field id={`info-person-${i}-role`} label="Role" value={p.role} onChange={setP(i, 'role')} placeholder="Director"/>
            <Field id={`info-person-${i}-email`} label="Email" type="email" value={p.email} onChange={setP(i, 'email')}/>
            <button type="button" onClick={() => onChange({ ...v, people: v.people.filter((_, j) => j !== i) })} className="h-10 px-2 text-stone-400 hover:text-red-600" aria-label="Remove person" data-testid={`info-person-${i}-remove`}><Trash2 size={16}/></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange({ ...v, people: [...v.people, { name: '', idNumber: '', role: '', email: '', phone: '' }] })}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 hover:text-emerald-900" data-testid="info-add-person-btn"><Plus size={15}/> Add person</button>
      </div>

      <label className="flex items-start gap-2.5 text-sm text-stone-700 rounded-lg border border-stone-200 bg-stone-50 p-3">
        <Checkbox checked={!!v.efilingAccess} onCheckedChange={(c) => onChange({ ...v, efilingAccess: !!c })} className="mt-0.5" data-testid="info-efiling-checkbox"/>
        <span>I will give the firm <b>tax practitioner access on SARS eFiling</b> for this entity. (Please never share your eFiling password.)</span>
      </label>

      {needsDebit && (
        <>
          <H>Debit order bank details</H>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><Field id="info-debit-holder" label="Account holder" value={v.debit.accountHolder} onChange={setD('accountHolder')}/></div>
            <Field id="info-debit-bank" label="Bank" value={v.debit.bank} onChange={setD('bank')}/>
            <Field id="info-debit-account" label="Account number" value={v.debit.accountNumber} onChange={setD('accountNumber')} inputMode="numeric"/>
            <Field id="info-debit-branch" label="Branch code" value={v.debit.branchCode} onChange={setD('branchCode')} inputMode="numeric"/>
            <div>
              <Label className="text-xs">Account type</Label>
              <Select value={v.debit.accountType || ''} onValueChange={setD('accountType')}>
                <SelectTrigger className="mt-1 h-10" data-testid="info-debit-type-select"><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>{ACC.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Debit day</Label>
              <Select value={v.debit.debitDay || ''} onValueChange={setD('debitDay')}>
                <SelectTrigger className="mt-1 h-10" data-testid="info-debit-day-select"><SelectValue placeholder="Day"/></SelectTrigger>
                <SelectContent>{['1', '7', '15', '25', '28'].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
